import React, { useEffect, useState } from "react";
import { StateBoxProps } from "./HSM";

export type SidebarTextInputProps = {
    placeholder: string; 
    text: string;
    setText: React.Dispatch<React.SetStateAction<string>>;
}

export const SidebarTextInput = ({
    placeholder,
    text,
    setText,
} : SidebarTextInputProps) => {
    return <textarea placeholder={placeholder} onChange={(e)=>setText(e.target.value)} value={text}/>;
}

export type SidebarProps = {
    title: string;
    description: string;
    children?: JSX.Element[];
    innerClassName: string;
    textInputs: any[];
    canSubmit: boolean;
    onSubmit: () => void;
    onCancel: () => void;
}

export const Sidebar = ({
    title,
    description,
    children = [],
    innerClassName,
    textInputs,
    canSubmit,
    onSubmit,
    onCancel,
} : SidebarProps) => {
    return (
        <div className='sidebar'>
            <h1>{title}</h1>
            <p>{description}</p>
            <div className={innerClassName}>
               {children}
            </div>
            <div className='sidebar-form'>
                {textInputs.map((prop) => <SidebarTextInput {...prop}/>)}
                <div className='sidebar-btns'>
                    {canSubmit && <button onClick={onSubmit} className='btn btn-primary mb-3'>
                        Submit
                    </button>}
                    {!canSubmit && <button onClick={onSubmit} className='btn btn-primary mb-3' disabled>
                        Submit
                    </button>}
                    <button onClick={onCancel} className='btn btn-danger mb-3'>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

export type SidebarContentProps = {
    title: string;
    description: string;
    text: string;
    modelInfo: string;
    textAsLink: boolean;
}

export const SidebarContentBox = ({
    title,
    description,
    text,
    modelInfo,
    textAsLink,
} : SidebarContentProps) => {
    const titleElmt = () => {
        if(textAsLink) {
            if (text.indexOf('http') === -1) {
                text = 'http://' + text;
            }
            return (
                <a target="_blank" href={text}><h2>{title}</h2></a>
            );
        } else {
            return <h2>{title}</h2>;
        }
    }

    const textElmt = () => {
        if(textAsLink) {
            return (
                <p>
                    <b>{'Model Information: '}</b>
                    {modelInfo === '' ? 'None' : modelInfo}
                </p>
            );;
        } else {
            return <p>{text}</p>;
        }
    }
    return (
        <div className='sidebar-content'>
            {titleElmt()}
            <h3>{description}</h3>
            {textElmt()}
        </div>
    );
}

export type CommentsSidebarProps = {
    stateProps: StateBoxProps; // props for the state being commented on
    // type: string;
    // stateText: string;
    children?: any[];
    addComment: (comment:string) => void;
    hideComments: () => void;
}

export const CommentsSidebar = ({
    stateProps,
    children=[],
    addComment,
    hideComments,
} : CommentsSidebarProps) => {
    const [commentText, setCommentText] = useState('');
    useEffect(() => {setCommentText('')}, [stateProps.id]);

    if(!stateProps) {
        hideComments();
        return null;
    }

    const stateType = stateProps.type;
    const stateText = stateProps.text;
    let textAreaProps : SidebarTextInputProps = {
        placeholder: 'Enter comment...',
        setText: setCommentText,
        text: commentText,
    }

    // let commentTextArea = <textarea {...textAreaProps}/>;

    let sidebarProps : SidebarProps = {
        title: 'Comments',
        innerClassName: 'comments',
        description: 'Comments for ' + stateType + ': "' + stateText + '"',
        textInputs: [textAreaProps],
        canSubmit: commentText !== '',
        onSubmit: () => {addComment(commentText); setCommentText('')},
        onCancel: hideComments,
    }

    return (<Sidebar {...sidebarProps}>
            {children.map((v) => {
                let contentProps : SidebarContentProps = {
                    title: v.author,
                    modelInfo: '',
                    description: v.occupation,
                    text: v.text,
                    textAsLink: false,
                }
                return <SidebarContentBox {...contentProps}/>;
            })}
        </Sidebar>);
}

export type PapersSidebarProps = {
    stateProps: StateBoxProps;
    children?: any[];
    addPaper: (title:string, url:string, modelInfo:string) => void;
    hidePapers: () => void;
}

export const PapersSidebar = ({
    stateProps,
    children=[],
    addPaper,
    hidePapers,
} : PapersSidebarProps) => {
    const [paperTitle, setPaperTitle] = useState('');
    const [paperUrl, setPaperUrl] = useState('');
    const [modelInfo, setModelInfo] = useState('');
    useEffect(() => {setPaperTitle(''); setPaperUrl(''); setModelInfo('')}, [stateProps.id]);

    if(!stateProps) {
        hidePapers();
        return null;
    }

    const stateType = stateProps.type;
    const stateText = stateProps.text;
    let titleTextAreaProps : SidebarTextInputProps = {
        placeholder: 'Enter paper title...',
        setText: setPaperTitle,
        text: paperTitle,
    }

    let urlTextAreaProps : SidebarTextInputProps = {
        placeholder: 'Enter paper URL...',
        setText: setPaperUrl,
        text: paperUrl,
    }

    let modelTextAreaProps : SidebarTextInputProps = {
        placeholder: '(optional) Enter user functionality model, robot model, caregiver model, and environment model, if exists...',
        setText: setModelInfo,
        text: modelInfo,
    }

    let sidebarProps : SidebarProps = {
        title: 'Related papers',
        innerClassName: 'papers',
        description: 'Research papers relating to ' + stateType + ': "' + stateText + '"',
        textInputs: [titleTextAreaProps, urlTextAreaProps, modelTextAreaProps],
        canSubmit: paperTitle !== '' && paperUrl !== '',
        onSubmit: () => {addPaper(paperTitle, paperUrl, modelInfo); setPaperTitle(''); setPaperUrl('');},
        onCancel: hidePapers,
    }

    return (<Sidebar {...sidebarProps}>
            {children.map((v) => {
                let contentProps : SidebarContentProps = {
                    title: v.title,
                    modelInfo: v.modelInfo,
                    description: 'Posted by ' + v.author,
                    text: v.url,
                    textAsLink: true
                }
                return <SidebarContentBox {...contentProps}/>;
            })}
        </Sidebar>);
}